import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Typography, Box,
  Table, TableBody, TableCell, TableHead, TableRow, Chip
} from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import verificareToken from '../VerificareToken';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * ResponsabiliDosar
 * Afișează istoricul responsabililor unui dosar via activityLog/{idDosar}/responsabili
 * Props:
 *   open       - boolean
 *   onClose    - funcție de închidere
 *   idDosar    - id-ul dosarului
 *   numarDosar - opțional, afișat în titlu
 */
function ResponsabiliDosar({ open, onClose, idDosar, numarDosar }) {
  const { token } = verificareToken();
  const [responsabili, setResponsabili] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !idDosar) return;

    const fetchResponsabili = async () => {
      setLoading(true);
      setError(null);
      setResponsabili([]);
      try {
        const response = await fetch(
          `${window.API_URL}activityLog/${idDosar}/responsabili`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Eroare la preluarea datelor (HTTP ${response.status})`);
        }

        const json = await response.json();
        const lista = json?.data?.istoricResponsabili ?? [];
        setResponsabili(lista);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResponsabili();
  }, [open, idDosar, token]);

  const formatData = (dateStr) => {
    if (!dateStr) return { data: '—', ora: null, complet: '—' };
    const [datePart, timePart] = dateStr.split(' ');
    if (!datePart) return { data: dateStr, ora: null, complet: dateStr };
    const [year, month, day] = datePart.split('-');
    const ora = timePart ? timePart.substring(0, 5) : null;
    const data = `${day}.${month}.${year}`;
    return { data, ora, complet: ora ? `${data} ${ora}` : data };
  };

  const getEventLabel = (event) => {
    switch (event) {
      case 'created':   return { label: 'Creare dosar', color: 'info' };
      case 'assigned':  return { label: 'Atribuire',    color: 'primary' };
      case 'removed':   return { label: 'Eliminat',     color: 'error' };
      case 'updated':   return { label: 'Actualizare',  color: 'warning' };
      default:          return { label: event || '—',   color: 'default' };
    }
  };

  // rândurile plate pentru export
  const getExportRows = () =>
    responsabili.map((r) => {
      const d = formatData(r.assigned_at);
      return {
        jurist: r.user_fullname || r.user_name || '—',
        username: r.user_name || '—',
        atribuitDe: r.assigned_by_fullname || '—',
        dataAtribuirii: d.complet,
        eveniment: getEventLabel(r.event).label,
        stare: r.is_current ? 'Activ' : 'Anterior',
      };
    });

  const titluFisier = numarDosar
    ? `Responsabili_dosar_${numarDosar}`
    : `Responsabili_dosar_${idDosar}`;

  // ── Export Excel ──────────────────────────────────────────
  const handleExportExcel = async () => {
    const rows = getExportRows();
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Responsabili dosar');

    // header
    ws.columns = [
      { header: 'Jurist',           key: 'jurist',          width: 28 },
      { header: 'Username',         key: 'username',        width: 18 },
      { header: 'Atribuit de',      key: 'atribuitDe',      width: 28 },
      { header: 'Data atribuirii',  key: 'dataAtribuirii',  width: 20 },
      { header: 'Eveniment',        key: 'eveniment',       width: 18 },
      { header: 'Stare',            key: 'stare',           width: 12 },
    ];

    // stilizare header
    ws.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    rows.forEach((r) => ws.addRow(r));

    // zebra + aliniere
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: rowNumber % 2 === 0 ? 'FFF5F5F5' : 'FFFFFFFF' },
        };
      });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${titluFisier}.xlsx`);
  };

  // ── Export PDF ────────────────────────────────────────────
  const handleExportPDF = () => {
    const rows = getExportRows();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFontSize(13);
    doc.text(
      `Responsabili dosar${numarDosar ? ': ' + numarDosar : ''}`,
      40, 40
    );

    autoTable(doc, {
      startY: 60,
      head: [['Jurist', 'Username', 'Atribuit de', 'Data atribuirii', 'Eveniment', 'Stare']],
      body: rows.map((r) => [
        r.jurist, r.username, r.atribuitDe, r.dataAtribuirii, r.eveniment, r.stare,
      ]),
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      styles: { fontSize: 9, cellPadding: 5 },
      columnStyles: { 0: { cellWidth: 120 }, 2: { cellWidth: 120 } },
    });

    doc.save(`${titluFisier}.pdf`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="responsabili-dosar-title"
    >
      <DialogTitle id="responsabili-dosar-title" className="titluModal">
        <Box display="flex" alignItems="center" gap={1}>
          <SupervisorAccountIcon fontSize="small" />
          <span>
            Responsabili dosar
            {numarDosar ? `: ${numarDosar}` : ''}
          </span>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && responsabili.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Nu există date despre responsabilii acestui dosar.
          </Typography>
        )}

        {!loading && !error && responsabili.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Jurist</strong></TableCell>
                <TableCell><strong>Atribuit de</strong></TableCell>
                <TableCell><strong>Data atribuirii</strong></TableCell>
                <TableCell><strong>Eveniment</strong></TableCell>
                <TableCell><strong>Stare</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {responsabili.map((r, index) => {
                const ev = getEventLabel(r.event);
                const dataAtribuire = formatData(r.assigned_at);
                return (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {r.user_fullname || r.user_name || '—'}
                      </Typography>
                      {r.user_fullname && r.user_name && (
                        <Typography variant="caption" color="textSecondary">
                          ({r.user_name})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.assigned_by_fullname || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {dataAtribuire.data}
                        </Typography>
                        {dataAtribuire.ora && (
                          <Chip
                            label={dataAtribuire.ora}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ev.label}
                        size="small"
                        color={ev.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {r.is_current ? (
                        <Chip label="Activ" size="small" color="success" />
                      ) : (
                        <Chip label="Anterior" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box display="flex" gap={1}>
          <Button
            onClick={handleExportExcel}
            disabled={responsabili.length === 0}
            size="small"
            variant="outlined"
            color="success"
            startIcon={<TableChartIcon />}
          >
            Export Excel
          </Button>
          <Button
            onClick={handleExportPDF}
            disabled={responsabili.length === 0}
            size="small"
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdfIcon />}
          >
            Export PDF
          </Button>
        </Box>
        <Button onClick={onClose} color="primary" variant="outlined" size="small">
          Închide
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ResponsabiliDosar;
